import React from 'react';
import styled, { keyframes } from 'styled-components';

// 스켈레톤 애니메이션
const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

// 기본 스켈레톤 컴포넌트
const SkeletonBase = styled.div`
  background: #f0f0f0;
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    animation: ${shimmer} 1.5s infinite;
  }
`;

// 클래스 카드 스켈레톤
export const ClassCardSkeleton: React.FC = () => (
  <ClassSkeletonContainer>
    <ClassImageSkeleton />
    <ClassInfoSkeleton>
      <ClassTitleSkeleton />
      <ClassCaptionSkeleton />
    </ClassInfoSkeleton>
  </ClassSkeletonContainer>
);

// 커피챗 카드 스켈레톤 (클래스와 동일)
export const CoffeeChatCardSkeleton: React.FC = () => (
  <ClassSkeletonContainer>
    <ClassImageSkeleton />
    <ClassInfoSkeleton>
      <ClassTitleSkeleton />
      <ClassCaptionSkeleton />
    </ClassInfoSkeleton>
  </ClassSkeletonContainer>
);

// 아카이브 카드 스켈레톤
export const ArchiveCardSkeleton: React.FC = () => (
  <ArchiveSkeletonContainer>
    <ArchiveContentSkeleton>
      <ArchiveTitleSkeleton />
      <ArchiveAuthorSkeleton>
        <ArchiveAvatarSkeleton />
        <ArchiveAuthorTextSkeleton />
        <ArchiveDateSkeleton />
      </ArchiveAuthorSkeleton>
    </ArchiveContentSkeleton>
  </ArchiveSkeletonContainer>
);

// 커뮤니티 카드 스켈레톤
export const CommunityCardSkeleton: React.FC = () => (
  <CommunitySkeletonContainer>
    <CommunityContentSkeleton>
      <CommunityMainSkeleton>
        <CommunityTitleSkeleton />
        <CommunityBodySkeleton />
        <CommunityBodySkeleton style={{ width: '70%' }} />
        <CommunityCategoriesSkeleton>
          <CategorySkeleton />
          <CategorySkeleton />
        </CommunityCategoriesSkeleton>
      </CommunityMainSkeleton>
      <CommunityMetaSkeleton>
        <CommunityAuthorSkeleton>
          <ArchiveAvatarSkeleton />
          <ArchiveAuthorTextSkeleton />
          <ArchiveDateSkeleton />
        </CommunityAuthorSkeleton>
        <CommunityStatsSkeleton>
          <StatSkeleton />
          <StatSkeleton />
        </CommunityStatsSkeleton>
      </CommunityMetaSkeleton>
    </CommunityContentSkeleton>
  </CommunitySkeletonContainer>
);

// 클래스 스켈레톤 스타일
const ClassSkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ClassImageSkeleton = styled(SkeletonBase)`
  width: 100%;
  height: 188px;
  border-radius: 12px;
`;

const ClassInfoSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ClassTitleSkeleton = styled(SkeletonBase)`
  height: 24px;
  width: 85%;
`;

const ClassCaptionSkeleton = styled(SkeletonBase)`
  height: 18px;
  width: 65%;
`;

// 아카이브 스켈레톤 스타일
const ArchiveSkeletonContainer = styled.div`
  flex: 1;
  border-top: 1px solid var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
`;

const ArchiveContentSkeleton = styled.div`
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ArchiveTitleSkeleton = styled(SkeletonBase)`
  height: 26px;
  width: 90%;
`;

const ArchiveAuthorSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ArchiveAvatarSkeleton = styled(SkeletonBase)`
  width: 24px;
  height: 24px;
  border-radius: 50%;
`;

const ArchiveAuthorTextSkeleton = styled(SkeletonBase)`
  height: 20px;
  width: 60px;
`;

const ArchiveDateSkeleton = styled(SkeletonBase)`
  height: 20px;
  width: 120px;
`;

// 커뮤니티 스켈레톤 스타일
const CommunitySkeletonContainer = styled.div`
  width: 100%;
  border-top: 1px solid var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
`;

const CommunityContentSkeleton = styled.div`
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CommunityMainSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CommunityTitleSkeleton = styled(SkeletonBase)`
  height: 24px;
  width: 70%;
`;

const CommunityBodySkeleton = styled(SkeletonBase)`
  height: 22px;
  width: 100%;
`;

const CommunityCategoriesSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategorySkeleton = styled(SkeletonBase)`
  height: 20px;
  width: 80px;
`;

const CommunityMetaSkeleton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CommunityAuthorSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CommunityStatsSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatSkeleton = styled(SkeletonBase)`
  height: 20px;
  width: 60px;
`;
